import React, { useEffect, useState } from 'react';
import { ZoButton } from '@zonofi/zo-components/input/zo-button/zo-button';
import { ZoInputText } from '../zo-input-text-temp/zo-input-text-temp';
import { ZoTooltip } from '../zo-tooltip-temp/zo-tooltip-temp';
import './company-details-screen.scss';
import { SVGS } from '../assets';
import Button from '@mui/material/Button';
import { SignUpContainer } from '../sign-up-container/sign-up-container';
import { SellerDetails } from '@zonofi/zo-types';
import {
  useEventTracking,
  useLazyGetAllStatesQuery,
} from '@zonofi/seller-rtk-store';
import { CustomSvg } from '@zonofi/gandalf-utilities';
import _, { isEmpty } from 'lodash';
import { ZoSelect } from '../zo-select-temp/zo-select-temp';
import { JTBDEventType } from '@zonofi/seller-rtk-store';

interface CompanyDetailsScreenProps {
  goBack: () => void;
  setShowContactDetails: () => void;
  sellerDetails: SellerDetails;
  onUpdateValue: (key: string, value: string) => void;
  navigateToBusinessCategory: () => void;
}

export const CompanyDetailsScreen: React.FC<CompanyDetailsScreenProps> = ({
  goBack,
  setShowContactDetails,
  sellerDetails,
  navigateToBusinessCategory,
  onUpdateValue,
}) => {
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [states, setStates] = useState<{ label: string; value: string }[]>([]);
  const [getStatesReq, getStatesRes] = useLazyGetAllStatesQuery();
  const [errors, setErrors] = useState<{
    pinCode?: string;
    email?: string;
  }>({});
  const { trackEvent } = useEventTracking();
  const validateFields = () => {
    const newErrors: {
      companyName?: string;
      pinCode?: string;
      contactName?: string;
      email?: string;
    } = {};

    if (sellerDetails?.pinCode && sellerDetails.pinCode?.length !== 6) {
      newErrors.pinCode = 'Pin Code must be 6 digits';
    }

    if (!sellerDetails?.contactName?.trim()) {
      newErrors.contactName = 'Contact name is required';
    }

    // Email is mandatory when it wasn't already captured during sign-up.
    if (!sellerDetails?.isEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!sellerDetails?.emailId?.trim()) {
        newErrors.email = 'Email is required';
      } else if (!emailRegex.test(sellerDetails.emailId.trim())) {
        newErrors.email = 'Enter a valid email';
      }
    }

    setErrors(newErrors);

    return Object.keys(newErrors)?.length === 0;
  };
  useEffect(() => {
    getStatesReq();
  }, []);
  useEffect(() => {
    if (getStatesRes?.isSuccess) {
      const list: {
        value: string;
        label: string;
      }[] = [];
      getStatesRes?.data?.forEach((state) => {
        if (state?.name && state?.stateCode) {
          list.push({
            label: state?.name,
            value: state?.name,
          });
        }
      });
      setStates(list);
    }
  }, [getStatesRes]);
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        if (validateFields()) {
          setShowContactDetails();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [validateFields]);
  return (
    <SignUpContainer customClass="custom">
      <div className="company-details-screen">
        <div className="company-details-screen-header">
          <h2 className="primary-font-semibold seller-font-size-heading heading">
            Company Details
          </h2>
          <span className="primary-font-regular seller-font-size-medium sub-heading">
            Your registered company information as per GST records.
          </span>
        </div>
        <div className="detailsSection">
          <div className="detailstop_sec">
            <h3 className="primary-font-semibold">Details</h3>
            <div className="company-details-grid">
              <div className="company-detail-item">
                <span className="icon">
                  <CustomSvg
                    svg={SVGS.CompanyNameIconComponent}
                    height={20}
                    width={20}
                    colors={{
                      fill: '#8C8C8C',
                    }}
                    className="GstinIcon"
                  />
                </span>
                <div className="content">
                  <label>Company Name</label>
                  <span className="primary-font-regular seller-font-size-medium company-detail-item-text company-name">
                    {_.capitalize(sellerDetails?.companyName || '')}
                  </span>
                </div>
              </div>
              {sellerDetails?.gstin && sellerDetails?.gstin?.length !== 0 && (
                <div className="company-detail-item">
                  <span className="icon">
                    <CustomSvg
                      svg={SVGS.GstinIconComponent}
                      height={20}
                      width={20}
                      colors={{
                        fill: '#8C8C8C',
                      }}
                      className="GstinIcon"
                    />
                  </span>
                  <div className="content">
                    <label>GSTIN</label>
                    <span className="primary-font-regular seller-font-size-medium company-detail-item-text">
                      {sellerDetails?.gstin}{' '}
                      <img
                        src={SVGS.SuccessTick}
                        width={20}
                        height={20}
                        className="SuccessTick"
                      />
                    </span>
                  </div>
                </div>
              )}
              {sellerDetails?.gstValidTill && (
                <div className="company-detail-item">
                  <span className="icon">
                    <CustomSvg
                      svg={SVGS.CalendarIconComponent}
                      height={20}
                      width={20}
                      colors={{
                        fill: '#8C8C8C',
                      }}
                      className="GstinIcon"
                    />
                  </span>
                  <div className="content">
                    <label>Valid Till</label>
                    <span className="primary-font-regular seller-font-size-medium company-detail-item-text">
                      {sellerDetails?.gstValidTill}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="detailsTrade_sec_wrapper">
            <CustomSvg
              svg={SVGS.UserIconComponent}
              height={20}
              width={20}
              colors={{
                fill: '#8C8C8C',
              }}
              className="GstinIcon"
            />
            <div className="detailsTrade_sec">
              <ZoInputText
                title="Contact Name"
                placeholder="Enter Contact Name"
                value={sellerDetails?.contactName}
                required={true}
                onBlur={() => {}}
                inputFieldProps={{
                  value: sellerDetails?.contactName,
                  onChange: (e: { target: { value: string } }) =>
                    onUpdateValue('contactName', e.target.value),
                }}
                name={''}
                extraTitle={
                  <ZoTooltip
                    title={
                      'Example: Registered name is "abc" but the brand name can be "xyz."'
                    }
                    arrowDirection="right"
                  >
                    <img src={SVGS.InfoGreyFillIcon} />
                  </ZoTooltip>
                }
              />
            </div>
          </div>
          {!sellerDetails?.isEmail && (
            <div className="detailsTrade_sec_wrapper">
              <CustomSvg
                svg={SVGS.EmailIconComponent}
                height={20}
                width={20}
                colors={{
                  fill: '#8C8C8C',
                }}
                className="GstinIcon"
              />
              <div className="detailsTrade_sec">
                <ZoInputText
                  title="Email"
                  placeholder="Enter Email"
                  value={sellerDetails?.emailId}
                  required={true}
                  onBlur={() => {}}
                  inputFieldProps={{
                    value: sellerDetails?.emailId,
                    onChange: (e: { target: { value: string } }) =>
                      onUpdateValue('emailId', e.target.value),
                  }}
                  name={'emailId'}
                  error={(errors?.email?.length || 0) > 0}
                />
                {(errors?.email?.length || 0) > 0 && (
                  <div className="gst-err-msg primary-font-regular">
                    <img
                      src={SVGS.ErrorIcon}
                      width={16}
                      height={16}
                      className="ErrorIcon"
                    />
                    {errors?.email}{' '}
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="detailAddress_sec">
            <div className="headingSec">
              <h3 className="primary-font-semibold">Company Address</h3>
              {!isEditingAddress && (
                <Button
                  className="editbutton"
                  variant="text"
                  onClick={() => setIsEditingAddress(true)}
                >
                  Edit
                </Button>
              )}
            </div>

            {!isEditingAddress ? (
              <div className="addressSection">
                <span className="icon">
                  <img
                    src={SVGS.Location}
                    width={20}
                    height={20}
                    className="Location"
                  />
                </span>
                <div className="content">
                  <label>Address</label>
                  <div className="primary-font-regular">
                    {sellerDetails?.address
                      ? `${sellerDetails?.address}, `
                      : ''}{' '}
                    {sellerDetails?.city ? `${sellerDetails?.city}, ` : ''}
                    {sellerDetails?.state ? `${sellerDetails?.state}, ` : ''}
                    {sellerDetails?.pinCode
                      ? `- ${sellerDetails?.pinCode}`
                      : ''}
                  </div>
                </div>
              </div>
            ) : (
              <div className="editableAddressSectionWrapper">
                <CustomSvg
                  svg={SVGS.LocationIconComponent}
                  height={20}
                  width={20}
                  colors={{
                    fill: '#8C8C8C',
                  }}
                  className="GstinIcon"
                />
                <div className="editableAddressSection">
                  <div className="editaddres_top">
                    <ZoInputText
                      placeholder="Enter Address"
                      value={sellerDetails?.address}
                      title="Address"
                      onBlur={() => {}}
                      inputFieldProps={{
                        value: sellerDetails?.address,
                        onChange: (e: { target: { value: string } }) =>
                          onUpdateValue('address', e.target.value),
                      }}
                      name=""
                    />
                  </div>
                  <div className="editaddress_bottom">
                    <ZoInputText
                      name="city"
                      title="City"
                      placeholder="Enter City"
                      value={sellerDetails?.city}
                      onBlur={() => {}}
                      inputFieldProps={{
                        value: sellerDetails?.city,
                        onChange: (e: { target: { value: string } }) => {
                          const value = e.target.value.replace(
                            /[^a-zA-Z\s]/g,
                            ''
                          );
                          onUpdateValue('city', value);
                        },
                      }}
                    />
                    <ZoSelect
                      title="State"
                      value={sellerDetails?.state}
                      placeholder="Select State"
                      onSelectValue={(val: string) =>
                        onUpdateValue('state', val)
                      }
                      options={states}
                    />
                    <div className="pincode_sec">
                      <ZoInputText
                        title="Pin Code"
                        placeholder="000000"
                        value={sellerDetails?.pinCode}
                        onBlur={() => {}}
                        inputFieldProps={{
                          value: sellerDetails?.pinCode,
                          onChange: (e: { target: { value: string } }) => {
                            const newValue = e.target.value;
                            if (/^\d{0,6}$/.test(newValue)) {
                              onUpdateValue('pinCode', newValue);
                            }
                          },
                        }}
                        endAdornment={
                          <span
                            className="text-gray-500"
                            style={{
                              fontSize: '12px',
                              lineHeight: '16px',
                              color: '#8c8c8c',
                            }}
                          >
                            {sellerDetails?.pinCode?.length || 0}/6{' '}
                          </span>
                        }
                        name=""
                        error={(errors?.pinCode?.length || 0) > 0}
                      />
                      {(errors?.pinCode?.length || 0) > 0 && (
                        <div className="gst-err-msg primary-font-regular">
                          <img
                            src={SVGS.ErrorIcon}
                            width={16}
                            height={16}
                            className="ErrorIcon"
                          />
                          {errors?.pinCode}{' '}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="details_bottomSec">
          <Button className="backbutton" variant="text" onClick={goBack}>
            <span>
              <img
                src={SVGS.ArrowLeft}
                width={20}
                height={20}
                className="ArrowLeft"
              />
            </span>{' '}
            Back
          </Button>
          <ZoButton
            label="Continue"
            endIcon={
              <SVGS.RightArrowComponent
                // height={20}
                // width={20}
                fill={
                  isEmpty(sellerDetails?.companyName) ||
                  isEmpty(sellerDetails?.contactName)
                    ? '#454545'
                    : '#FFFFFF'
                }
                stroke={
                  isEmpty(sellerDetails?.companyName) ||
                  isEmpty(sellerDetails?.contactName)
                    ? '#454545'
                    : '#FFFFFF'
                }
                className="GstinIcon"
              />
            }
            // onClick={() => console.log('Continue with company details')}
            onClick={() => {
              if (validateFields()) {
                trackEvent(JTBDEventType.SIGNUP_COMPANY_DETAILS_PROVIDED, {
                  ...sellerDetails,
                });
                navigateToBusinessCategory();
                // setShowContactDetails();
              }
            }}
            className="sign-up-now-btn"
            disabled={
              sellerDetails?.companyName?.length === 0 ||
              sellerDetails?.contactName?.length === 0 ||
              (!sellerDetails?.isEmail && !sellerDetails?.emailId?.trim())
            }
          />
        </div>
      </div>
    </SignUpContainer>
  );
};
